import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { ATHENA } from "@/constants/testIds";

const SCIENCE_BG =
  "https://images.unsplash.com/photo-1781330173674-c7e809277fbe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxzY2llbnRpc3RzJTIwbW9kZXJuJTIwbGFib3JhdG9yeXxlbnwwfHx8fDE3ODE2Mjc0NTF8MA&ixlib=rb-4.1.0&q=85";

const STAGES = [
  ["DISCOVERY", "Botanical & molecular screening. AI-assisted target identification."],
  ["CHARACTERISATION", "Spectrometry, structural elucidation, purity & potency assays."],
  ["FORMULATION", "Bioavailability engineering, encapsulation, delivery system design."],
  ["VALIDATION", "Pre-clinical & IRB-approved clinical studies with academic partners."],
];

export const Science = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section
      id="science"
      data-testid={ATHENA.scienceSection}
      className="relative bg-[#0A1713] text-[#F8F7F4] overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={SCIENCE_BG}
          alt="Scientists in lab"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-[#0A1713]/85" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="label-mono">// R&amp;D PHILOSOPHY</span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="font-display text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.04] tracking-tight"
            >
              Science with
              <br />
              consequence.
            </motion.h2>
            <p className="mt-8 text-[#A2B3AB] leading-relaxed max-w-md">
              Our four-stage process is the discipline behind every product we
              ship. Each compound passes through traceable workflows — every
              batch carries a full chain of custody, every claim is backed by
              data we can defend.
            </p>
            <button
              data-testid={ATHENA.scienceCta}
              onClick={() => scrollTo("contact")}
              className="btn-primary btn-on-dark mt-10"
            >
              Discuss a research brief <ArrowDownRight size={16} strokeWidth={1.5} />
            </button>
          </div>

          {/* Pipeline visualisation */}
          <div className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-[#1A382A]">
            <ol className="space-y-10">
              {STAGES.map((s, i) => (
                <motion.li
                  key={s[0]}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="grid grid-cols-12 gap-4 items-start pb-10 border-b border-[#1A382A] last:border-b-0"
                >
                  <div className="col-span-2 md:col-span-1 font-mono text-[#B89B64] text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-10 md:col-span-4">
                    <div className="font-display text-xl md:text-2xl leading-tight whitespace-nowrap">{s[0]}</div>
                  </div>
                  <div className="col-span-12 md:col-span-7 text-[#A2B3AB] leading-relaxed">
                    {s[1]}
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Science;
